import DashboardTablePreview from "@/components/Dashboard/DashboardTablePreview";
import { Link } from "@/components/Link";
import { MilestoneDateEditor } from "@/containers/Project/components";
import * as S from "@/containers/Project/components/styled";
import dayjs from "dayjs";
import { routes } from "@/helpers";
import {
  calculateCanEditMilestoneActualDate,
  calculateCanEditMilestoneTargetDates,
  calculateCanUserModifyProjectStatus,
} from "@/providers/helpers";
import {
  useGetKeyMilestoneDashboardQuery,
  useConfigurationQuery,
} from "@/services";
import { ShrinkOutlined, ArrowsAltOutlined } from "@ant-design/icons";
import { Button, Table, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

const canUserModifyProject = ({ projectStatus }) =>
  calculateCanUserModifyProjectStatus(projectStatus);

const KeyMilestonesPreview = () => {
  const intl = useIntl();

  const {
    data: { data: projects } = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetKeyMilestoneDashboardQuery();

  const { data: configuration } = useConfigurationQuery({});
  const keyMilestoneDashboardEnabled =
    configuration?.keyMilestoneDashboardEnabled === true;

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [hasInitializedExpandedRows, setHasInitializedExpandedRows] =
    useState(false);

  useEffect(() => {
    if (projects?.length && !hasInitializedExpandedRows) {
      setExpandedRowKeys([projects[0].projectPublicId]);
      setHasInitializedExpandedRows(true);
    }
  }, [projects, hasInitializedExpandedRows]);

  const toggleExpandAll = () => {
    if (expandedRowKeys?.length) {
      setExpandedRowKeys([]);
    } else {
      setExpandedRowKeys(
        projects?.map(({ projectPublicId }) => projectPublicId),
      );
    }
  };

  const milestoneColumns = [
    {
      title: intl.formatMessage({ id: "dashboard.columns.milestone" }),
      dataIndex: "name",
      width: 220,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: intl.formatMessage({ id: "dashboard.columns.abbreviation" }),
      dataIndex: "abbreviation",
      width: 60,
      sorter: (a, b) =>
        (a.abbreviation || "").localeCompare(b.abbreviation || ""),
    },
    {
      title: intl.formatMessage({ id: "dashboard.columns.schedule" }),
      dataIndex: "scheduleName",
      width: 80,
      sorter: (a, b) =>
        (a.scheduleName || "").localeCompare(b.scheduleName || ""),
    },
    {
      title: intl.formatMessage({ id: "dashboard.columns.status" }),
      dataIndex: "statusCode",
      align: "center",
      width: 75,
      sorter: (a, b) =>
        (a.statusCode || "").localeCompare(b.statusCode || ""),
      render: (status, { hasParent }) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <S.StyledStatusIconContainer>
            <div
              className={`status-${status?.toLowerCase()} ${
                hasParent ? "" : "major"
              }`}
            />
          </S.StyledStatusIconContainer>
        </div>
      ),
    },
    {
      title: intl.formatMessage({ id: "dashboard.columns.target" }),
      dataIndex: "targetDate",
      width: 120,
      sorter: (a, b) => new Date(a.targetDate) - new Date(b.targetDate),
      render: (_, milestone) => (
        <MilestoneDateEditor
          data-test-id="key-milestone-date-edit"
          milestone={{
            ...milestone,
            id: milestone.milestonePublicId,
            projectId: milestone.projectPublicId,
            status: milestone.statusCode,
          }}
          intlId="projectMilestoneTable.target"
          datePropertyName="targetDate"
          disabled={
            !calculateCanEditMilestoneTargetDates(
              {
                status: milestone.statusCode,
                targetDateLocked: milestone.targetDateLocked,
              },
              canUserModifyProject(milestone),
              milestone.isProjectComplete,
            )
          }
          onSaved={refetch}
        />
      ),
    },
    {
      title: intl.formatMessage({
        id: keyMilestoneDashboardEnabled
          ? "dashboard.columns.actualCurrentDate"
          : "dashboard.columns.actualDate",
      }),
      dataIndex: "actualDate",
      width: 120,
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      sorter: (a, b) => new Date(a.actualDate) - new Date(b.actualDate),
      render: (_, milestone) => (
        <MilestoneDateEditor
          data-test-id="key-milestone-date-edit"
          milestone={{
            ...milestone,
            id: milestone.milestonePublicId,
            projectId: milestone.projectPublicId,
            status: milestone.statusCode,
          }}
          intlId={
            keyMilestoneDashboardEnabled
              ? "projectMilestoneTable.actualCurrent"
              : "projectMilestoneTable.actual"
          }
          datePropertyName="actualDate"
          disabled={
            !calculateCanEditMilestoneActualDate(
              {
                status: milestone.statusCode,
                targetDateLocked: milestone.targetDateLocked,
              },
              canUserModifyProject(milestone),
              milestone.isProjectComplete,
            )
          }
          onSaved={refetch}
        />
      ),
    },
    {
      title: intl.formatMessage({ id: "dashboard.columns.daysSinceUpdated" }),
      dataIndex: "actualDateUpdatedAt",
      align: "center",
      width: 110,
      sorter: (a, b) => (a.actualDateUpdatedAt || 0) - (b.actualDateUpdatedAt || 0),
      render: (actualDateUpdatedAt) => actualDateUpdatedAt ? dayjs().diff(dayjs(actualDateUpdatedAt), "day") : "",
    },
  ];

  return (
    <DashboardTablePreview
      data={projects}
      isLoading={isLoading}
      isFetching={isFetching}
      title={intl.formatMessage({
        id: "dashboard.titles.keyMilestones",
      })}
      totalCount={projects?.length}
      rowKey={({ projectPublicId }) => projectPublicId}
      columns={[
        {
          title: intl.formatMessage({ id: "dashboard.columns.projectNumber" }),
          dataIndex: "projectNumber",
          width: 80,
          onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
          sorter: (a, b) =>
            (a.projectNumber || "").localeCompare(b.projectNumber || ""),
          render: (projectNumber, { projectPublicId }) => (
            <Link to={routes.projects.get(projectPublicId)}>
              {projectNumber}
            </Link>
          ),
        },
        {
          title: intl.formatMessage({ id: "dashboard.columns.projectName" }),
          dataIndex: "projectName",
          sorter: (a, b) =>
            (a.projectName || "").localeCompare(b.projectName || ""),
        },
      ]}
      expandable={{
        columnTitle: (
          <Tooltip
            title={intl.formatMessage({
              id: expandedRowKeys?.length
                ? "dashboard.collapseAll"
                : "dashboard.expandAll",
            })}
          >
            <Button
              type="text"
              size="small"
              icon={
                expandedRowKeys?.length ? (
                  <ShrinkOutlined />
                ) : (
                  <ArrowsAltOutlined />
                )
              }
              onClick={toggleExpandAll}
              data-test-id="key-milestones-toggle-expand-all"
            />
          </Tooltip>
        ),
        expandedRowRender: ({ milestones, projectPublicId, projectStatus, isProjectComplete }) => (
          <Table
            columns={milestoneColumns}
            dataSource={milestones?.map((milestone) => ({
              ...milestone,
              projectPublicId,
              projectStatus,
              isProjectComplete,
            }))}
            rowKey={({ milestonePublicId }) => milestonePublicId}
            pagination={false}
            tableLayout="fixed"
            scroll={{ x: true }}
          />
        ),
        rowExpandable: ({ milestones }) => !!milestones?.length,
        expandedRowKeys,
        onExpandedRowsChange: setExpandedRowKeys,
      }}
    />
  );
};

export default KeyMilestonesPreview;

